import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { Link } from '@atlassian/react-resource-router';

const CustomHeading = (props: { children: React.ReactNode }) => (
	<Heading as="h3" size="small">
		{props.children}
	</Heading>
);

export type Props = {
	iconUrl: string;
	link: string;
	title: string;
	tooltip: string;
};

export const SidebarHeader = ({ iconUrl, link, title, tooltip }: Props) => (
	<Box data-testId="object-name-link">
		<ObjectNameLink target="_blank" href={link}>
			<ObjectIcon data-testId="object-name-icon">
				{/* Using a key set to the icon url prevents this image from being
                        re-used and causing the old image to appear while the new one is loading */}
				<img src={iconUrl} key={iconUrl} alt="" />
			</ObjectIcon>
			<ObjectName>{title}</ObjectName>
			<Box data-testId="object-name-tooltip">
				<Tooltip content={tooltip}>
					<Box data-testId="object-name-shortcut-icon">
						<ShortcutIcon spacing="spacious" label="" />
					</Box>
				</Tooltip>
			</Box>
		</ObjectNameLink>
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ObjectNameLink = styled(Link)({
	display: 'flex',
	alignItems: 'center',
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: {
		opacity: 0,
		transition: 'opacity 200ms ,transform 200ms',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		transform: `translateX(-${gridSize}px)`,
		marginLeft: token('space.100'),
	},
	'&:focus, &:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		color: token('color.link'),

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			opacity: 1,
			transform: 'translateX(0)',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ObjectIcon = styled.div({
	flexShrink: 0,
	width: token('space.300'),
	height: token('space.300'),
	marginRight: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	img: {
		width: '100%',
		height: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ObjectName = styled(CustomHeading)({
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	color: token('color.text.subtle'),
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
	wordBreak: 'break-all',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		color: token('color.link'),
	},
});
